
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

interface Settings {
  invertAlpha: boolean;
  animationSpeed: number;
  gradientWidth: number;
  gradientStrength: number;
  highlightColor: string;
  blendMode: string;
}

@Component
export default class SpotGlossSimulator extends Vue {
  // Props for image sources
  @Prop({ type: String, required: true }) readonly baseImageSrc!: string;
  @Prop({ type: String, required: true }) readonly overlayImageSrc!: string | Blob;

  // Settings with default values
  settings: Settings = {
    invertAlpha: false,
    animationSpeed: 4,
    gradientWidth: 50,
    gradientStrength: 0.9,
    highlightColor: '#fff5e1',
    blendMode: 'normal'
  };

  // Overlay data URL after processing
  overlayDataURL: string | null = null;

  // Original image dimensions
  naturalWidth: number = 0;
  naturalHeight: number = 0;

  // Scaled dimensions
  scaledWidth: number = 0;
  scaledHeight: number = 0;

  // Default settings for reset
  defaultSettings: Settings = {
    invertAlpha: false,
    animationSpeed: 4,
    gradientWidth: 50,
    gradientStrength: 0.9,
    highlightColor: '#fff5e1',
    blendMode: 'normal'
  };

  blendModeOptions: Array<{ text: string; value: string }> = [
    { text: 'Normal', value: 'normal' },
    { text: 'Darken', value: 'darken' },
    { text: 'Multiply', value: 'multiply' },
    { text: 'Color Burn', value: 'color-burn' },
    { text: 'Lighten', value: 'lighten' },
    { text: 'Screen', value: 'screen' },
    { text: 'Color Dodge', value: 'color-dodge' },
    { text: 'Overlay', value: 'overlay' },
    { text: 'Soft Light', value: 'soft-light' },
    { text: 'Hard Light', value: 'hard-light' },
    { text: 'Difference', value: 'difference' },
    { text: 'Exclusion', value: 'exclusion' },
    { text: 'Hue', value: 'hue' },
    { text: 'Saturation', value: 'saturation' },
    { text: 'Color', value: 'color' },
    { text: 'Luminosity', value: 'luminosity' }
  ];

  // Loading and error states
  isLoading: boolean = false;
  error: string | null = null;

  // Lifecycle hooks
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.processBaseImage(this.baseImageSrc);
    this.processOverlayImage(this.overlayImageSrc);
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  }

  // Watchers to react to props changes
  @Watch('baseImageSrc')
  onBaseImageSrcChange(newVal: string) {
    this.processBaseImage(newVal);
  }

  @Watch('overlayImageSrc')
  onOverlayImageSrcChange(newVal: string) {
    this.processOverlayImage(newVal);
  }

  // Watchers to react to settings changes
  @Watch('settings', { deep: true })
  onSettingsChange() {
    this.updateSpotGloss();
  }

  // Process Base Image
  async processBaseImage(dataURL: string) {
    this.isLoading = true;
    this.error = null;
    try {
      const img = await this.loadImage(dataURL);
      this.naturalWidth = img.naturalWidth;
      this.naturalHeight = img.naturalHeight;
      this.scaleToFit();
    } catch (error) {
      console.error('Error loading base image:', error);
      this.error = 'Failed to load base image.';
    } finally {
      this.isLoading = false;
    }
  }

  // Process Overlay Image
  async processOverlayImage(imgURL: string | Blob) {
    this.isLoading = true;
    this.error = null;
    try {
      const dataUrl = await this.convertImageToDataUrl(imgURL);
      const maskDataURL = await this.generateMask(
        dataUrl,
        this.settings.invertAlpha,
        10 // maskBlur is fixed to 10 as per original code
      );
      this.overlayDataURL = maskDataURL;
    } catch (error) {
      console.error('Error processing overlay image:', error);
      this.error = 'Failed to process overlay image. Ensure it is CORS-enabled.';
    } finally {
      this.isLoading = false;
    }
  }

  // Load Image as Promise with crossOrigin set to 'Anonymous'
  loadImage(dataURL: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Set crossOrigin to 'Anonymous' to enable CORS
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataURL;
    });
  }

  // Convert Image URL to Data URL via Canvas
  async convertImageToDataUrl(imgURL: string | Blob): Promise<string> {
    try {
      let blob: Blob = new Blob();
      // Fetch the image from the URL
      if (imgURL instanceof Blob) {
        blob = imgURL as Blob;
      } else {
        const response = await fetch(imgURL, { mode: 'cors' });

        // Check if the fetch was successful
        if (!response.ok) {
          throw new Error(`Failed to fetch image. Status: ${response.status}`);
        }

        // Get the image as a Blob
        blob = await response.blob();
      }

      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        // Define the onload event for the FileReader
        reader.onloadend = () => {
          resolve(reader.result as string);
        };

        // Define the onerror event for the FileReader
        reader.onerror = () => {
          reject(new Error('Failed to convert Blob to Data URL.'));
        };

        // Read the Blob as a Data URL
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to Data URL:', error);
      throw error;
    }
  }

  // Scale image to fit viewport
  scaleToFit() {
    const maxW = window.innerWidth * 0.8;
    const maxH = window.innerHeight * 0.7;
    let scale = 1;
    if (this.naturalWidth > maxW) {
      scale = maxW / this.naturalWidth;
    }
    if (this.naturalHeight * scale > maxH) {
      scale = maxH / this.naturalHeight;
    }
    this.scaledWidth = this.naturalWidth * scale;
    this.scaledHeight = this.naturalHeight * scale;
  }

  // Handle window resize
  handleResize() {
    if (this.baseImageSrc) {
      this.scaleToFit();
      this.updateSpotGloss();
    }
  }

  // Update Spot Gloss (Mask and Gradient)
  updateSpotGloss() {
    // Re-process the overlay image to apply new settings
    if (this.overlayDataURL) {
      this.processOverlayImage(this.overlayDataURL);
    }
  }

  // Generate mask using Canvas
  generateMask(dataURL: string, invertAlpha: boolean, maskBlur: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Unable to get canvas context'));
            return;
          }

          if (maskBlur > 0) {
            // Create temporary canvas for blurring
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = img.width;
            tempCanvas.height = img.height;
            const tempCtx = tempCanvas.getContext('2d');
            if (tempCtx) {
              tempCtx.filter = `blur(${maskBlur}px)`;
              tempCtx.drawImage(img, 0, 0);
              ctx.drawImage(tempCanvas, 0, 0);
            } else {
              ctx.drawImage(img, 0, 0);
            }
          } else {
            ctx.drawImage(img, 0, 0);
          }

          const imageData = ctx.getImageData(0, 0, img.width, img.height);
          const data = imageData.data;

          for (let i = 0; i < data.length; i += 4) {
            let a = data[i + 3];
            if (invertAlpha) a = 255 - a;
            data[i + 3] = a;
          }

          ctx.putImageData(imageData, 0, 0);
          const newDataUrl = canvas.toDataURL('image/png');
          resolve(newDataUrl);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => {
        reject(new Error('Failed to load overlay image for masking'));
      };
      img.src = dataURL;
    });
  }

  // Set preset color
  setPresetColor(color: string) {
    this.settings.highlightColor = this.rgbToHex(color);
  }

  // Reset settings to default
  resetSettings() {
    this.settings = { ...this.defaultSettings };
  }

  // Convert Hex to RGBA
  hexToRGBA(hex: string, alpha: number): string {
    let r: number, g: number, b: number;

    if (hex.startsWith('rgb')) {
      const rgbMatch = hex.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (rgbMatch) {
        r = parseInt(rgbMatch[1], 10);
        g = parseInt(rgbMatch[2], 10);
        b = parseInt(rgbMatch[3], 10);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
    }

    hex = hex.replace(/^#/, '');
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(c => c + c)
        .join('');
    }
    const num = parseInt(hex, 16);
    r = (num >> 16) & 255;
    g = (num >> 8) & 255;
    b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // Convert RGB to Hex
  rgbToHex(rgb: string): string {
    const rgbMatch = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1], 10);
      const g = parseInt(rgbMatch[2], 10);
      const b = parseInt(rgbMatch[3], 10);
      return (
        '#' +
        ((1 << 24) + (r << 16) + (g << 8) + b)
          .toString(16)
          .slice(1)
          .toUpperCase()
      );
    }
    return rgb; // Return original if not in rgb() format
  }

  // Computed styles for spotGloss
  get spotGlossStyles() {
    const gradientCSS = this.generateGradientCSS();
    return {
      backgroundImage: gradientCSS, // Set the gradient as the background image
      backgroundPosition: '0% 0%', // Initial position
      backgroundSize: '200% 100%', // Size of the gradient
      animationName: 'shimmer',
      animationDuration: `${this.settings.animationSpeed}s`,
      animationIterationCount: 'infinite',
      animationTimingFunction: 'linear',
      maskImage: this.overlayDataURL ? `url(${this.overlayDataURL})` : 'none',
      WebkitMaskImage: this.overlayDataURL ? `url(${this.overlayDataURL})` : 'none',
      filter: 'blur(10px)',
      mixBlendMode: this.settings.blendMode
    };
  }

  // Generate Gradient CSS based on settings
  generateGradientCSS(): string {
    const angle = 150; // Fixed as per original code
    const strength = this.settings.gradientStrength;
    const color = this.settings.highlightColor;
    const width = this.settings.gradientWidth;

    const midStop = this.hexToRGBA(color, strength);
    const startPoint = 50 - width / 2;
    const endPoint = 50 + width / 2;

    return `linear-gradient(
        ${angle}deg,
        rgba(255,255,255,0) 0%,
        rgba(255,255,255,0) ${startPoint}%,
        ${midStop} 50%,
        rgba(255,255,255,0) ${endPoint}%,
        rgba(255,255,255,0) 100%
      )`;
  }
}
